/**
 * NonFactoryCategory — the shared per-category page for every category
 * except "3d-segmentation" (Reporter Studies / Triage Validation / Other),
 * rendered for ALL roles.
 *
 * These categories are NOT factory-managed: there's no accept/request-more/
 * enrollments/phases surface for them, just the existing labeling projects
 * served by `/api/labeling/projects` (real staging data). This is a flat,
 * reporter-page-style square list — the exact idiom shipped in
 * `ReportingStudyHome.tsx` — not the factory annotator/admin/internal views
 * `CategoryView` still routes "3d-segmentation" through.
 *
 * A reporting project opens its `ReportingStudyHome` case list; everything
 * else goes straight into the labeling viewer via `/labeling?project=`.
 *
 * "triage-validation" is the one exception to the flat list (Task 13):
 * triage is really a couple of overarching studies (a2z-Omni-Triage `ot-*`,
 * a2z-Abdo-Triage `abdct-*`) each with many batches, so it renders as
 * project→batches groups (`TriageProjectGroup`, inheriting the 3D-seg
 * factory's `ProjectWorklistGroup` nested look) instead of a ~33-row flat
 * list. Reporter Studies and Other keep the flat list below unchanged.
 */

import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { listUrl } from "@/types/jointReview";

import {
  useProgressQuery,
  useProjectsQuery,
  type ProjectSummary,
} from "../../../hooks/useLabelingQueries";
import {
  CATEGORIES,
  categoryForProjectType,
  type LabelingCategoryKey,
} from "../../../utils/labelingCategories";
import { groupProjects, groupEvalBatchesByTask } from "../../../utils/studyGroups";
import { FaChevronRight } from "../../../utils/icons";
import { categoryAccent } from "./categoryAccent";
import { CountStrip, LoadingPanel, ProgressMeter, SquareList, SquareRow } from "./primitives";
import { TriageProjectGroup } from "./TriageProjectGroup";

/**
 * Blue completion progress bar (Task 15 — Andrea feedback: staging-style
 * bars instead of the raw `XXX/YYY` count). This is the labels/cases ratio
 * from the already-loaded projects list, NOT a real per-project stats %
 * (that would need `useProjectStatsQuery` per project — out of scope here).
 * `total_cases` absent/0 renders "—" — never a fabricated 0% bar; a known
 * `total_cases` with a missing `total_labels` clamps done to 0 (a real "0
 * of N done" state, not a guess).
 */
function LiveCompletionCell({ project }: { project: ProjectSummary }) {
  const progressQuery = useProgressQuery(project.project_id);
  const total = progressQuery.data?.total_labels ?? project.total_cases;

  if (!total || total <= 0 || progressQuery.isLoading || progressQuery.error) {
    return <span className="shrink-0 font-mono text-xs text-fg-muted">—</span>;
  }

  return (
    <span className="shrink-0">
      <ProgressMeter
        done={progressQuery.data?.completed_labels ?? 0}
        total={total}
        display="percent"
      />
    </span>
  );
}

function CompletionCell({ project }: { project: ProjectSummary }) {
  // Segmentation-study worklists set stats.total_labels to the number of
  // labels EXPECTED. It is not a completion count. Read the live progress
  // endpoint for these studies so a fresh 10-case study renders 0%, not 100%.
  if (project.project_type === "segmentation_study") {
    return <LiveCompletionCell project={project} />;
  }
  if (project.project_type === "joint_review") {
    const progress = project.joint_review_progress;
    return progress ? (
      <span className="shrink-0 font-mono text-xs tabular-nums text-fg-muted">
        {progress.final_items} of {progress.total_items} final
      </span>
    ) : (
      <span role="alert" className="shrink-0 text-xs text-warn">
        Progress missing
      </span>
    );
  }

  const total = project.total_cases;
  if (!total || total <= 0) {
    return <span className="shrink-0 font-mono text-xs text-fg-muted">—</span>;
  }
  const done = Math.min(project.total_labels ?? 0, total);
  return (
    <span className="shrink-0">
      <ProgressMeter done={done} total={total} display="percent" />
    </span>
  );
}

interface NonFactoryCategoryProps {
  categoryKey: LabelingCategoryKey;
}

export function NonFactoryCategory({ categoryKey }: NonFactoryCategoryProps) {
  const navigate = useNavigate();
  const meta = CATEGORIES.find((c) => c.key === categoryKey)!;
  const accent = categoryAccent(categoryKey);

  const projectsQuery = useProjectsQuery();

  const projects = useMemo(() => {
    const all = projectsQuery.data?.projects ?? [];
    return all
      .filter((p) => categoryForProjectType(p.project_type) === categoryKey)
      .sort((a, b) => a.project_id.localeCompare(b.project_id));
  }, [projectsQuery.data, categoryKey]);

  const isTriage = categoryKey === "triage-validation";

  const triageGroups = useMemo(() => {
    if (!isTriage) return [];
    const byId = new Map(projects.map((p) => [p.project_id, p]));
    return groupProjects(projects.map((p) => p.project_id)).map((group) => ({
      key: group.key,
      label: group.label,
      batches: group.projects
        .map((id) => byId.get(id))
        .filter((p): p is ProjectSummary => p != null),
    }));
  }, [projects, isTriage]);

  const handleRowClick = (project: ProjectSummary) => {
    if (project.project_type === "reporting") {
      navigate(`/labeling/reporting/${encodeURIComponent(project.project_id)}`);
      return;
    }
    if (project.project_type === "segmentation_study") {
      navigate(`/labeling/segmentation-study/${encodeURIComponent(project.project_id)}`);
      return;
    }
    if (project.project_type === "joint_review") {
      navigate(listUrl(project.project_id));
      return;
    }
    navigate(`/labeling?project=${encodeURIComponent(project.project_id)}`);
  };

  const handleOpenBatch = (projectId: string) => {
    navigate(`/labeling?project=${encodeURIComponent(projectId)}`);
  };

  const countStripItems = isTriage
    ? [`${triageGroups.length} studies`, `${projects.length} batches`]
    : [`${projects.length} projects`];

  return (
    <div className="h-full overflow-auto text-white page-premium">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <button
          type="button"
          onClick={() => navigate("/labeling-overview")}
          className="mb-4 text-xs text-fg-muted transition hover:text-fg"
        >
          ← Labeling
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-2.5">
            <span
              aria-hidden="true"
              className={`w-0.5 self-stretch rounded-sharp ${accent.edge}`}
            />
            <h1 className="text-2xl font-bold text-fg">{meta.name}</h1>
          </div>
          <div className="mt-3">
            <CountStrip items={countStripItems} />
          </div>
        </div>

        {projectsQuery.isLoading ? (
          <LoadingPanel label="Loading projects…" />
        ) : projectsQuery.error ? (
          <div className="rounded border border-warn/30 bg-warn-tint p-8 text-center text-sm text-warn">
            Failed to load projects
          </div>
        ) : projects.length === 0 ? (
          <div className="rounded border border-border/50 bg-surface p-8 text-center text-sm text-fg-muted">
            No projects in this category yet
          </div>
        ) : isTriage ? (
          <div className="flex flex-col gap-3.5">
            {triageGroups.map((group) =>
              group.key === "chest-eval" || group.key === "head-eval" ? (
                <div key={group.key} className="flex flex-col gap-2">
                  <div className="flex items-baseline gap-2 px-1">
                    <span aria-hidden="true" className="font-mono text-fg-faint">
                      ◪
                    </span>
                    <span className="text-sm font-semibold text-fg">{group.label}</span>
                    <span className="font-mono text-xs text-fg-muted">{`${group.batches.length} batches`}</span>
                  </div>
                  {groupEvalBatchesByTask(group.batches).map((task) => (
                    <TriageProjectGroup
                      key={task.taskLabel}
                      label={task.taskLabel}
                      projects={task.batches}
                      onOpenBatch={handleOpenBatch}
                    />
                  ))}
                </div>
              ) : (
                <TriageProjectGroup
                  key={group.key}
                  label={group.label}
                  projects={group.batches}
                  onOpenBatch={handleOpenBatch}
                />
              ),
            )}
          </div>
        ) : (
          <SquareList>
            {projects.map((project) => (
              <SquareRow key={project.project_id} onClick={() => handleRowClick(project)}>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-fg">
                    {project.name || project.project_id}
                  </div>
                  <div className="truncate font-mono text-xs text-fg-muted">
                    {project.project_id}
                  </div>
                </div>
                <CompletionCell project={project} />
                <FaChevronRight className="h-3 w-3 shrink-0 text-fg-muted" />
              </SquareRow>
            ))}
          </SquareList>
        )}
      </div>
    </div>
  );
}

export default NonFactoryCategory;
